import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle, Share2, Printer, MapPin } from 'lucide-react';

const ContactAgent = () => {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Map Section */}
          <motion.div 
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="aspect-[4/3] bg-gray-200 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3612.4739!2d55.2708!3d25.2048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43496ad9c645%3A0xbde66e1084c4b436!2sDubai%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2sus!4v1635959000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              ></iframe>
            </div>
            
            {/* Map Overlay with Location Info */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="absolute bottom-4 left-4 bg-white rounded-lg p-3 sm:p-4 shadow-lg max-w-xs"
            >
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-800 text-sm sm:text-base">Park Gate Residences</div>
                  <div className="text-xs sm:text-sm text-gray-600">Al Kitaf, Dubai</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Agent Contact Section */}
          <motion.div 
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="space-y-6 lg:space-y-8 order-1 lg:order-2"
          >
            {/* Presented By Header */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 lg:mb-6 tracking-wider">
                PRESENTED BY
              </h2>
              
              {/* Agent Card */}
              <div className="bg-white rounded-xl lg:rounded-2xl p-4 sm:p-6 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gray-300 rounded-full overflow-hidden flex-shrink-0 mx-auto sm:mx-0">
                    <img 
                      src="https://images.pexels.com/photos/3785077/pexels-photo-3785077.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop"
                      alt="Anne Mereb"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">Anne Mereb</h3>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-all duration-300 text-sm"
                      >
                        <Phone className="w-4 h-4" />
                        <span className="font-semibold">CALL</span>
                      </motion.button>
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-all duration-300 text-sm"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="font-semibold">EMAIL</span>
                      </motion.button>
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 text-sm"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span className="font-semibold">WHATSAPP</span>
                      </motion.button>
                    </div>
                  </div>
                </div>

                {/* Agent Details Grid */}
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Job position:</div>
                      <div className="text-gray-800 text-sm sm:text-base">Lead Broker</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">License:</div>
                      <div className="text-gray-800 text-sm sm:text-base">53658</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Speaks:</div>
                      <div className="text-gray-800 text-sm sm:text-base">English, Arabic, Bengali</div>
                    </div>
                  </div>
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Service area:</div>
                      <div className="text-gray-800 text-sm sm:text-base">Al Bateen, Downtown D...</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Broker License:</div>
                      <div className="text-gray-800 text-sm sm:text-base">28873</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">Listing reference:</div>
                      <div className="text-gray-800 text-sm sm:text-base font-mono">XGB6538UHD56C</div>
                    </div>
                  </div>
                </div>

                {/* QR Code and DLD Section */}
                <div className="flex flex-col sm:flex-row items-start justify-between space-y-4 sm:space-y-0">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white rounded grid grid-cols-3 gap-px p-1">
                        {Array.from({ length: 9 }).map((_, i) => (
                          <div key={i} className={`bg-gray-800 ${Math.random() > 0.5 ? 'opacity-100' : 'opacity-30'}`}></div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-1">DLD Permit Number:</div>
                      <div className="text-gray-800 font-mono text-sm sm:text-base">12345678</div>
                    </div>
                  </div>
                  
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <div className="text-xs sm:text-sm font-semibold text-gray-600 mb-2">Print or share this property:</div>
                    <div className="flex space-x-2">
                      <motion.button 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-10 h-10 border-2 border-blue-500 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-all duration-300"
                      >
                        <Share2 className="w-4 h-4" />
                      </motion.button>
                      <motion.button 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-10 h-10 border-2 border-blue-500 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-all duration-300"
                      >
                        <Printer className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div 
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 lg:mt-16 text-center"
        >
          <div className="bg-slate-900 rounded-xl lg:rounded-2xl py-6 lg:py-8 px-6">
            <div className="flex items-center justify-center space-x-3">
              <span className="text-white text-base sm:text-lg">Powered by</span>
              <div className="text-white text-xl sm:text-2xl font-bold">
                MENA
                <span className="block text-xs sm:text-sm font-normal tracking-wider">HOMES</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactAgent;